import type { SystemPiece } from "@/features/search/api/use-search";
import type { PaletteDestination } from "./nav-items";

/**
 * A peneira do ⌘K, e a única razão pela qual ela precisa existir separada.
 *
 * ── O DEFEITO ─────────────────────────────────────────────────────────────
 *
 * A paleta comparava `label + hint + terms` com `includes` cru. Numa tela em
 * português isso falha nos DOIS sentidos, e falha calado:
 *
 *   - os `terms` das abas de Configurações estão escritos SEM acento, de
 *     propósito ("fuso horario", "saude", "versao", "presenca") — então quem
 *     digita a palavra certa, "fuso horário" ou "saúde", não achava nada;
 *   - os rótulos têm acento ("Configurações", "Missões") — então quem digita
 *     "configuracoes", que é como meio mundo digita, também não achava nada.
 *
 * Um dado escrito de um jeito e comparado de outro. A tabela de abas estava
 * certa ao escrever sem acento; o que faltava era a comparação combinar com ela.
 *
 * ── POR QUE DOBRAR, E NÃO CONSERTAR OS DADOS ──────────────────────────────
 *
 * Porque acentuar os `terms` resolveria só metade: "configuracoes" continuaria
 * sem achar "Configurações", e os rótulos não podem perder o acento — eles são
 * DESENHADOS. Dobrar na hora da comparação é o único lugar em que os dois lados
 * passam, e deixa quem escreve a próxima aba livre para digitar como quiser.
 *
 * Dobrar só ALARGA o que se acha: tudo que casava antes continua casando, porque
 * texto sem acento não muda ao ser dobrado.
 */

/**
 * O texto sem acento e em minúsculas, para comparar — nunca para desenhar.
 *
 * `NFD` separa a letra do acento e a classe `\p{Diacritic}` remove o acento que
 * ficou solto, o que cobre todo o português sem uma tabela de-para à mão. O `ç`
 * é decomposto em `c` + cedilha por essa mesma regra, e é por isso que "endereco"
 * acha "endereço".
 */
export function fold(text: string): string {
	return text
		.normalize("NFD")
		.replace(/\p{Diacritic}/gu, "")
		.toLowerCase();
}

/** Uma linha da paleta já em português (ou em inglês): o que ela desenha e busca. */
export interface DrawnDestination {
	readonly to: string;
	readonly label: string;
	readonly hint: string;
	readonly terms: string;
	readonly platformAdmin: boolean;
}

/**
 * A tradução, entregue de fora — `undefined` quando a chave não existe.
 *
 * Uma função e não o `t` do `vue-i18n` porque este arquivo é uma TABELA: ele
 * não monta componente, não tem instância e é lido por um teste que não monta
 * app nenhum. Quem tem o idioma ativo é quem desenha.
 */
export type Translate = (key: string) => string | undefined;

/**
 * As linhas da paleta na língua de agora — e é isto que a busca compara.
 *
 * A peneira (`matches`) roda DEPOIS daqui, de propósito: quem digita "invoice"
 * num painel em inglês e "fatura" num em português tem de chegar na mesma tela,
 * e comparar contra a chave crua não acharia nenhuma das duas.
 */
export function drawDestinations(
	rows: readonly PaletteDestination[],
	translate: Translate,
): DrawnDestination[] {
	return rows.map((row) => {
		const label = translate(row.labelKey) ?? row.fallback?.label ?? "";
		const prefix = row.prefixKey ? translate(row.prefixKey) : undefined;
		return {
			to: row.to,
			label: prefix ? `${prefix} · ${label}` : label,
			hint: translate(row.hintKey) ?? row.fallback?.hint ?? "",
			terms: (row.termsKey ? translate(row.termsKey) : undefined) ?? row.fallback?.terms ?? "",
			platformAdmin: row.platformAdmin,
		};
	});
}

/** O que uma linha da paleta oferece à busca. `terms` não é desenhado. */
export interface Searchable {
	readonly label: string;
	readonly hint: string;
	readonly terms?: string;
}

/**
 * Se esta linha responde ao que foi digitado.
 *
 * Uma busca vazia responde `true`: a paleta aberta sem nada digitado mostra os
 * destinos, e não uma lista vazia.
 */
export function matches(row: Searchable, needle: string): boolean {
	const wanted = fold(needle.trim());
	if (!wanted) return true;
	return fold(`${row.label} ${row.hint} ${row.terms ?? ""}`).includes(wanted);
}

/** Um pedaço do rótulo: o que casou com o que foi digitado, ou o que não casou. */
export interface LabelPart {
	readonly text: string;
	readonly hit: boolean;
}

/**
 * O RÓTULO PARTIDO NO PEDAÇO QUE CASOU — o `ts_headline` da V2, deste lado.
 *
 * ── POR QUE NÃO VEM DO SERVIDOR ───────────────────────────────────────────
 *
 * A V2 pedia o grifo ao Postgres e o desenhava com `v-html`: uma marcação vinda
 * do servidor, confiada por uma moldura que está em TODA tela do painel. O grifo
 * não precisa disso. A pergunta foi digitada aqui, os oito resultados já estão
 * aqui, e partir uma string em três é mais barato do que o `ts_headline` que teve
 * de rodar por linha do outro lado. O que sai são nós de texto — nunca HTML.
 *
 * ── POR QUE NÃO É UM `indexOf` DIRETO ─────────────────────────────────────
 *
 * Porque a peneira dobra o acento (`fold`) e o grifo tem de concordar com ela: se
 * "jose" acha "José" e o grifo não grifa nada, a linha aparece sem que ninguém
 * entenda por quê. Dobrar LETRA A LETRA é o que mantém um mapa de volta para o
 * texto original — dobrar a string inteira encurta ("á" vira "a") e todo índice
 * depois do primeiro acento passa a apontar para a letra errada.
 */
export function highlight(label: string, needle: string): LabelPart[] {
	const whole: LabelPart[] = [{ text: label, hit: false }];
	const wanted = fold(needle.trim());
	if (!wanted) return whole;

	let folded = "";
	/** Para cada letra do texto DOBRADO, de qual letra do original ela veio. */
	const origin: number[] = [];
	for (let i = 0; i < label.length; i++) {
		const one = fold(label[i] ?? "");
		for (let k = 0; k < one.length; k++) origin.push(i);
		folded += one;
	}

	const at = folded.indexOf(wanted);
	if (at < 0) return whole;

	const from = origin[at] ?? 0;
	// A letra SEGUINTE à última que casou: `+1` sobre a origem da última, e não a
	// origem da próxima — que não existe quando o casamento termina no fim do rótulo.
	const to = (origin[at + wanted.length - 1] ?? label.length - 1) + 1;

	return [
		{ text: label.slice(0, from), hit: false },
		{ text: label.slice(from, to), hit: true },
		{ text: label.slice(to), hit: false },
	].filter((part) => part.text.length > 0);
}

/**
 * As espécies que alguém procura pelo nome. O CAMPO entra ("endereço" não achava
 * nada) e abre o mapa no próprio campo; a ligação continua dentro da tabela.
 */
const PIECE_KINDS = new Set([
	"entity",
	"field",
	"view",
	"pipeline",
	"artifact",
	"automation",
	"report",
	"agent",
]);

/**
 * AS PEÇAS DE "SEU SISTEMA" como linhas da paleta, abertas no mapa (`/system?no=`).
 *
 * A segunda linha diz o que a peça é — "Tela · Agendamentos", "Automação" — e o
 * código só entra na peneira. O que a plataforma gerou sozinha (a entrada de um
 * canal) fica de fora: tem código no lugar do nome, e ninguém o procura.
 *
 * `shown` são as chaves de peça que a paleta JÁ oferece noutra seção (a tabela de
 * dados, o funil, a tela sob medida): a mesma peça em duas seções era a mesma
 * linha duas vezes, e uma delas empurrava as automações para fora do corte.
 */
export function drawPieces(
	pieces: readonly SystemPiece[],
	entityName: (code: string) => string,
	say: (key: string, params?: Record<string, string>) => string,
	shown: ReadonlySet<string> = new Set(),
): Array<Searchable & { key: string; to: string }> {
	return pieces
		.filter((piece) => PIECE_KINDS.has(piece.kind) && !piece.generated && !shown.has(piece.key))
		.map((piece) => {
			const kind = say(
				`palette.piece.${piece.kind === "view" && piece.detail === "component" ? "component" : piece.kind}`,
			);
			const owner = piece.parent?.startsWith("entity:") ? entityName(piece.parent.slice(7)) : null;
			return {
				key: `piece:${piece.key}`,
				label: piece.name,
				hint: owner ? say("palette.piece.of", { kind, owner }) : kind,
				terms: `${piece.code} ${kind}`,
				to: `/system?no=${encodeURIComponent(piece.key)}`,
			};
		});
}

/**
 * O QUE O NOME DIZ VEM PRIMEIRO. "unid" + Enter abria a tela "Agenda das unidades"
 * e não a tabela "Unidades": o empate caía na ordem das seções, e a das telas vem
 * antes. Agora a QUALIDADE do casamento decide, e só depois a espécie:
 * 0 = o nome é o que foi digitado; 1 = o nome começa assim; 2 = uma palavra dele
 * começa assim; 3 = o nome contém; 4 = casou por outro lado (dica, sinônimo).
 */
function rankOf(label: string, wanted: string): number {
	const name = fold(label);
	if (name === wanted) return 0;
	if (name.startsWith(wanted)) return 1;
	if (name.split(/[\s·,.\-/]+/).some((w) => w.startsWith(wanted))) return 2;
	return name.includes(wanted) ? 3 : 4;
}

/**
 * No empate de qualidade, a espécie: a tabela de dados, a tela, as outras peças,
 * os registros e por último os lugares do produto (ajustes) e as ações. Seção que
 * não está aqui (uma nova) vai para o fim, sem quebrar.
 */
const KIND_ORDER = [
	"entities",
	"screens",
	"funnels",
	"pieces",
	"record",
	"contact",
	"conversation",
	"product",
	"admin",
	"action",
];
const kindOf = (section: string) => {
	const at = KIND_ORDER.indexOf(section);
	return at < 0 ? KIND_ORDER.length : at;
};

/**
 * As linhas reordenadas pelo nome. A lista continua desenhada em seções, então
 * a seção sobe pelo MELHOR casamento que tem (e, no empate, pela espécie), e
 * dentro dela o melhor vem antes — o que garante que a primeira linha, a do
 * Enter, é o melhor casamento de todos. Sem nada digitado, a ordem de sempre.
 */
export function rankRows<T extends { label: string; section: string }>(
	rows: readonly T[],
	needle: string,
): T[] {
	const wanted = fold(needle.trim());
	if (!wanted) return [...rows];
	const ranked = rows.map((row, i) => ({ row, i, rank: rankOf(row.label, wanted) }));
	const best = new Map<string, number>();
	for (const r of ranked) best.set(r.row.section, Math.min(best.get(r.row.section) ?? 9, r.rank));
	const key = (r: (typeof ranked)[number]) => [
		best.get(r.row.section) ?? 9,
		kindOf(r.row.section),
		r.rank,
		r.i,
	];
	return ranked
		.sort((a, b) => {
			const [ka, kb] = [key(a), key(b)];
			for (let k = 0; k < ka.length; k++) if (ka[k] !== kb[k]) return (ka[k] ?? 0) - (kb[k] ?? 0);
			return 0;
		})
		.map((r) => r.row);
}
